import axios from 'axios';
import { createErr, createOk, Result } from '../types/result';
import { constructUrl } from './utils';
import { Crawler, isCrawlers, isCrawler } from '../types/crawler';
import {
  CrawlerDetail,
  isCrawlerDetail,
  isCrawlerDetails,
} from '../types/crawlerDetail';
import { AdhocSupportedInterval } from '../constants/adhocSupportedInterval';

/**
 * クローラー新規登録 payload
 */
export type CrawlerRegistrationPayload = {
  /** クローラーID */
  id: number | null;
  /** クローラー名 */
  name: string;
  /** イメージ名 */
  imageName: string;
  /** APIキーテンプレート */
  apiKeyTemplateId: number | null;
  /** バージョン */
  version: string;
  /** 説明 */
  description: string;
  /** リリース日 */
  releasedAt: string;
  /** 手動実行対応 */
  adhocSupportedIntervals: AdhocSupportedInterval[];
  /** UI表示テンプレート */
  configTemplate: string;
  /** 安定版 */
  stable: boolean;
  /** ベータ版 */
  beta: boolean;
};

/**
 * クローラー基本情報更新 payload
 */
export type CrawlerBaseEditPayload = {
  /** クローラー名 */
  name: string;
  /** イメージ名 */
  imageName: string;
  /** APIキーテンプレート */
  apiKeyTemplateId: number | null;
};

/**
 * クローラーバージョン追加 payload
 */
export type CrawlerDetailAddPayload = {
  /** バージョン */
  version: string;
  /** 説明 */
  description: string;
  /** リリース日 */
  releasedAt: string;
  /** 手動実行対応 */
  adhocSupportedIntervals: AdhocSupportedInterval[];
  /** UI表示テンプレート */
  configTemplate: string;
  /** 安定版 */
  stable: boolean;
  /** ベータ版 */
  beta: boolean;
};

/**
 * クローラーバージョン更新 payload
 */
export type CrawlerDetailEditPayload = {
  /** 説明 */
  description: string;
  /** リリース日 */
  releasedAt: string;
  /** 手動実行対応 */
  adhocSupportedIntervals: AdhocSupportedInterval[];
  /** UI表示テンプレート */
  configTemplate: string;
  /** 安定版 */
  stable: boolean;
  /** ベータ版 */
  beta: boolean;
};

const URL = '/v1/crawlers';

/**
 * クローラー一覧を取得する
 */
export const fetchCrawlers = async (): Promise<Result<Crawler[], Error>> =>
  axios
    .get(constructUrl(URL))
    .then(({ data }: { data: unknown }) => {
      if (!isCrawlers(data)) {
        throw Error(`Unsupported Value: ${JSON.stringify(data)}`);
      }

      return createOk(data);
    })
    .catch((err: Error) => createErr(err));

/**
 * クローラーを新規登録する
 */
export const addCrawler = async (
  payload: CrawlerRegistrationPayload,
): Promise<Result<Crawler, Error>> =>
  axios
    .post(constructUrl(URL), payload)
    .then(({ data }) => createOk(data))
    .catch((err: Error) => createErr(err));

/**
 * クローラーを削除する
 */
export const deleteCrawler = async (
  id: Crawler['id'],
): Promise<Result<null, Error>> =>
  axios
    .delete(constructUrl(`${URL}/${id}`))
    .then(() => createOk(null))
    .catch((err: Error) => createErr(err));

/**
 * クローラー基本情報を更新する
 */
export const editCrawler = async (
  id: Crawler['id'],
  payload: CrawlerBaseEditPayload,
): Promise<Result<Crawler, Error>> =>
  axios
    .put(constructUrl(`${URL}/${id}`), payload)
    .then(({ data }: { data: unknown }) => {
      if (!isCrawler(data)) {
        throw Error(`Unsupported Value: ${JSON.stringify(data)}`);
      }

      return createOk(data);
    })
    .catch((err: Error) => createErr(err));

/**
 * クローラーバージョン情報を取得する
 */
export const fetchCrawlerDetails = async (
  id: Crawler['id'],
): Promise<Result<CrawlerDetail[], Error>> =>
  axios
    .get(constructUrl(`${URL}/${id}/details`))
    .then(({ data }: { data: CrawlerDetail[] }) => {
      if (!isCrawlerDetails(data)) {
        throw Error(`Unsupported Value: ${JSON.stringify(data)}`);
      }

      return createOk(data);
    })
    .catch((err: Error) => createErr(err));

/**
 * クローラーバージョンを追加する
 */
export const addCrawlerDetail = (
  id: Crawler['id'],
  payload: CrawlerDetailAddPayload,
): Promise<Result<CrawlerDetail, Error>> =>
  axios
    .post(constructUrl(`${URL}/${id}/details`), payload)
    .then(({ data }: { data: unknown }) => {
      if (!isCrawlerDetail(data)) {
        throw Error(`Unsupported Value: ${JSON.stringify(data)}`);
      }

      return createOk(data);
    })
    .catch((err: Error) => createErr(err));

/**
 * クローラーバージョンを更新する
 */
export const editCrawlerDetail = async (
  id: Crawler['id'],
  version: CrawlerDetail['version'],
  payload: CrawlerDetailEditPayload,
): Promise<Result<CrawlerDetail, Error>> =>
  axios
    .put(constructUrl(`${URL}/${id}/details/${version}`), payload)
    .then(({ data }: { data: unknown }) => {
      if (!isCrawlerDetail(data)) {
        throw Error(`Unsupported Value: ${JSON.stringify(data)}`);
      }

      return createOk(data);
    })
    .catch((err: Error) => createErr(err));

/**
 * クローラーバージョンを削除する
 */
export const deleteCrawlerDetail = async (
  id: Crawler['id'],
  version: CrawlerDetail['version'],
): Promise<Result<null, Error>> =>
  axios
    .delete(constructUrl(`${URL}/${id}/details/${version}`))
    .then(() => createOk(null))
    .catch((err: Error) => createErr(err));
